/**
 * Version Debug Info Component - Compact Version
 * Only visible in development mode
 */

import { memo } from 'react';
import { Badge } from '@/components/ui/badge';
import { RefreshCw } from 'lucide-react';

// Get build-time constants with proper fallbacks
// Use the variables defined in vite.config.ts
const APP_VERSION = import.meta.env.VITE_APP_VERSION;
const NODE_ENV = import.meta.env.NODE_ENV || 'development';
const IS_PRODUCTION = import.meta.env.PROD;

interface VersionDebugInfoProps {
  onCheckVersion?: () => void;
  hasUpdate?: boolean;
  latestVersion?: string;
  isChecking?: boolean;
  error?: string | null;
}

export const VersionDebugInfo = memo<VersionDebugInfoProps>(
  ({
    onCheckVersion,
    hasUpdate = false,
    latestVersion,
    isChecking = false,
    error,
  }) => {
    // Only show in development
    if (IS_PRODUCTION) {
      return null;
    }

    // Debug environment variables in development
    if (import.meta.env.DEV) {
    }

    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-md bg-gray-900/90 px-3 py-2 text-xs text-white backdrop-blur-sm">
        <Badge variant="outline" className="border-gray-600 text-[10px]">
          {NODE_ENV.toUpperCase()}
        </Badge>

        <span className="text-gray-400">
          v{APP_VERSION}
          {!import.meta.env.APP_VERSION &&
            !import.meta.env.VITE_APP_VERSION && (
              <span className="ml-1 text-red-400" title="APP_VERSION not set">
                ⚠
              </span>
            )}
        </span>

        {latestVersion && (
          <>
            <span className="text-gray-600">→</span>
            <span className={hasUpdate ? 'text-yellow-400' : 'text-gray-400'}>
              v{latestVersion}
            </span>
          </>
        )}

        {hasUpdate && (
          <Badge className="border-yellow-500/30 bg-yellow-500/20 text-[10px] text-yellow-400">
            UPDATE
          </Badge>
        )}

        {error && (
          <Badge className="border-red-500/30 bg-red-500/20 text-[10px] text-red-400">
            ERROR
          </Badge>
        )}

        {onCheckVersion && (
          <button
            onClick={onCheckVersion}
            disabled={isChecking}
            className="ml-1 text-gray-400 transition-colors hover:text-white disabled:opacity-50"
            title="Check for updates"
          >
            <RefreshCw
              className={`h-3 w-3 ${isChecking ? 'animate-spin' : ''}`}
            />
          </button>
        )}
      </div>
    );
  },
);

VersionDebugInfo.displayName = 'VersionDebugInfo';
